/**
 * Admin per-venue cancellation policy tab (F42 venue detail).
 *
 * Surfaces + edits a venue's RESOLVED cancellation policy. When the venue has
 * no override the platform default is shown (`source: 'platform_default'`);
 * editing + saving creates a venue override; "Reset to platform default" clears
 * it. Writes through `PATCH /admin/venues/:id/cancellation-policy`
 * (If-Match-guarded on `venues.updated_at`, audited to `venue_admin_audit`,
 * fires the owner `venue_admin_override_applied` notification). Composes the
 * shared `<CancellationPolicyEditor>` (tier list + add/remove/reset/validation)
 * + `<CancelTierTrack>` (live refund preview) — the same controls the venue
 * owner uses in the portal. Gated on `bookings:edit_cancellation_policy`; without
 * it the editor renders read-only.
 *
 * Note: this is a plain override — the venue owner can still re-edit the policy
 * in the portal (no field lock applied).
 */
import { useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  CancellationPolicyEditor,
  CancelTierTrack,
  Card,
  Dialog,
  FormField,
  TextInput,
  useToast,
} from '@bravemove/ui-web';
import {
  CancellationPolicySchema,
  type AdminVenueDetail,
  type CancellationPolicy,
  type CancellationPolicyTier,
  type Money,
} from '@bravemove/types';
import { adminVenuesApi } from '../api/adminVenues';
import { translateApiError } from '../auth/errors.js';

/** Sample basePrice for the live preview — $50.00, matching the platform-default page. */
const SAMPLE_BASE_PRICE: Money = { amount: 5000, currency: 'USD' };
const SAMPLE_HOURS_TO_START = 24;

interface VenueCancellationPolicyTabProps {
  readonly detail: AdminVenueDetail;
  readonly canEdit: boolean;
  /** Updated detail after a successful save / reset. */
  readonly onSaved: (next: AdminVenueDetail) => void;
  /** Full reload of detail (used after a stale-conflict). */
  readonly onReload: () => void;
}

function errorCode(err: unknown): string | undefined {
  return err && typeof err === 'object' && 'body' in err
    ? (err as { body?: { error?: { code?: string } } }).body?.error?.code
    : undefined;
}

export function VenueCancellationPolicyTab({
  detail,
  canEdit,
  onSaved,
  onReload,
}: VenueCancellationPolicyTabProps) {
  const { t } = useI18n();
  const toast = useToast();

  const persistedTiers = detail.cancellationPolicy.tiers;
  const isOverride = detail.cancellationPolicy.source === 'venue_override';

  // Local draft (controlled by the editor). Keyed remount on detail.updatedAt
  // is handled by the parent so this re-initialises after every save.
  const [tiers, setTiers] = useState<ReadonlyArray<CancellationPolicyTier>>(persistedTiers);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [staleOpen, setStaleOpen] = useState(false);

  const valid = useMemo(() => CancellationPolicySchema.safeParse({ tiers }).success, [tiers]);
  const dirty = useMemo(
    () => JSON.stringify(tiers) !== JSON.stringify(persistedTiers),
    [tiers, persistedTiers],
  );

  const save = async (policy: CancellationPolicy | null) => {
    setSaving(true);
    try {
      const next = await adminVenuesApi.setCancellationPolicy(
        detail.id,
        { policy, ...(reason.trim() ? { reason: reason.trim() } : {}) },
        detail.updatedAt,
      );
      toast.push({
        tone: 'success',
        title:
          policy === null
            ? t('admin:venue.f42.cancellation.resetDone', {
                defaultValue: 'Reset to the platform default.',
              })
            : t('admin:venue.f42.cancellation.saved', {
                defaultValue: 'Cancellation policy saved.',
              }),
      });
      onSaved(next);
    } catch (err) {
      if (errorCode(err) === 'STALE_VENUE') setStaleOpen(true);
      else toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 pt-4" data-testid="admin-venue-cancellation-tab">
      <Banner tone="info">
        {isOverride
          ? t('admin:venue.f42.cancellation.sourceOverride', {
              defaultValue: 'Custom override — this venue uses its own cancellation tiers.',
            })
          : t('admin:venue.f42.cancellation.sourceDefault', {
              defaultValue:
                'Platform default — this venue has no custom policy; the platform-wide default applies.',
            })}
      </Banner>

      <Card>
        <h3 className="mb-1.5 text-[13px] font-semibold text-ink">
          {t('admin:venue.f42.cancellation.heading', { defaultValue: 'Cancellation policy' })}
        </h3>
        <p className="mb-3 text-[12px] text-ink-3">
          {t('admin:venue.f42.cancellation.intro', {
            defaultValue:
              'The refund tiers applied when a player cancels a booking at this venue. Editing creates a venue-specific override; reset to revert to the platform default.',
          })}
        </p>

        <CancellationPolicyEditor tiers={tiers} onChange={setTiers} readOnly={!canEdit} />

        <div
          className="mt-4 border-t border-hairline pt-3"
          data-testid="admin-venue-cancellation-preview"
        >
          <h4 className="mb-2 text-[12.5px] font-semibold text-ink">
            {t('cancellation:policy.preview_heading', { defaultValue: 'Refund preview' })}
          </h4>
          <CancelTierTrack
            policy={{ tiers }}
            hoursToStart={SAMPLE_HOURS_TO_START}
            basePrice={SAMPLE_BASE_PRICE}
          />
        </div>

        {canEdit ? (
          <div className="mt-4 flex flex-col gap-3 border-t border-hairline pt-3">
            <div className="max-w-sm">
              <FormField
                label={t('admin:venue.f42.cancellation.reasonLabel', {
                  defaultValue: 'Reason (optional)',
                })}
              >
                <TextInput
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="admin-venue-cancellation-reason"
                />
              </FormField>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                size="sm"
                loading={saving}
                disabled={!dirty || !valid}
                onClick={() => void save({ tiers: [...tiers] })}
                data-testid="admin-venue-cancellation-save"
              >
                {t('admin:venue.f42.cancellation.save', { defaultValue: 'Save policy' })}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                loading={saving}
                disabled={!isOverride}
                onClick={() => void save(null)}
                data-testid="admin-venue-cancellation-reset"
              >
                {t('admin:venue.f42.cancellation.reset', {
                  defaultValue: 'Reset to platform default',
                })}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-4 border-t border-hairline pt-3">
            <Banner tone="info">
              {t('admin:venue.f42.cancellation.readOnly', {
                defaultValue:
                  "Read-only — you don't have permission to edit the cancellation policy.",
              })}
            </Banner>
          </div>
        )}
      </Card>

      <Dialog
        open={staleOpen}
        onClose={() => setStaleOpen(false)}
        onConfirm={() => {
          setStaleOpen(false);
          onReload();
        }}
        title={t('admin:venue.f42.stale.title', { defaultValue: 'Venue changed' })}
        confirmLabel={t('admin:venue.f42.stale.reload', { defaultValue: 'Reload latest' })}
        cancelLabel={t('admin:common.cancel', { defaultValue: 'Cancel' })}
      >
        <p className="text-[13px] text-ink-2" data-testid="admin-venue-cancellation-stale">
          {t('admin:venue.f42.stale.body', {
            defaultValue:
              'This venue was modified since you opened it. Reload to get the latest before editing.',
          })}
        </p>
      </Dialog>
    </div>
  );
}
